/**
 * Agency → Alerts: every alert BlueHQ sends, who gets each one and how (email, text or both — and always on
 * their Home). Narcan: uses reported, and supervisors verifying the Narcan form was completed.
 */
import { useState } from 'react';
import { api } from '../shared/api.ts';
import { useRouter } from '../shared/router.tsx';
import { Badge, Card, ErrorBox, Notice, PageHeader, Spinner, useAction, useLoad } from '../shared/ui.tsx';
import { ago, at, day, EmptyState } from './common.tsx';

function ContactEdit({ p, carriers, onDone }: { p: any; carriers: any[]; onDone: () => void }) {
  const [f, setF] = useState({ email: p.email ?? '', cell: p.cell ?? '', carrier: p.carrier ?? '' });
  const a = useAction();
  return (
    <form
      className="al-contact"
      onSubmit={(e) => {
        e.preventDefault();
        a.run(async () => {
          await api.post(`/api/agency-alerts/contacts/${p.id}`, { email: f.email || null, cell: f.cell || null, carrier: f.carrier || null });
          onDone();
        });
      }}
    >
      <strong>{p.name}</strong>
      <input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="Email for alerts" aria-label={`Email for ${p.name}`} />
      <input value={f.cell} onChange={(e) => setF({ ...f, cell: e.target.value })} placeholder="Cell (10 digits)" aria-label={`Cell for ${p.name}`} inputMode="tel" />
      <select value={f.carrier} onChange={(e) => setF({ ...f, carrier: e.target.value })} aria-label={`Carrier for ${p.name}`}>
        <option value="">Carrier (only if texts go by email-to-text)</option>
        {carriers.map((c) => (
          <option key={c.key} value={c.key}>
            {c.label}
          </option>
        ))}
      </select>
      <button className="btn primary small" disabled={a.busy}>
        Save
      </button>
      <ErrorBox error={a.error} />
    </form>
  );
}

function KindCard({ k, people, carriers, onChange }: { k: any; people: any[]; carriers: any[]; onChange: () => void }) {
  const a = useAction();
  const [add, setAdd] = useState({ userId: '', byEmail: true, byText: false });
  const [editing, setEditing] = useState<string | null>(null);
  const save = (userId: string, byEmail: boolean, byText: boolean) => a.run(async () => (await api.post('/api/agency-alerts/subscriptions', { userId, kind: k.kind, byEmail, byText }), onChange()));
  const taken = new Set(k.people.map((x: any) => x.userId));
  const person = (id: string) => people.find((p) => p.id === id);
  return (
    <Card title={k.label} actions={<span className="small muted">{k.last30 ? `${k.last30} sent in the last 30 days` : 'None sent in the last 30 days'}</span>}>
      <div className="stack sm">
        <p className="small" style={{ margin: 0 }}>
          {k.about}
        </p>
        {k.always && <p className="small muted" style={{ margin: 0 }}>Always: {k.always}</p>}
        {k.people.length === 0 ? (
          <p className="small muted" style={{ margin: 0 }}>No one gets this alert yet.</p>
        ) : (
          <div className="table-wrap">
            <table className="table al-table">
              <thead>
                <tr>
                  <th>Who</th>
                  <th>Email</th>
                  <th>Text</th>
                  <th>Home</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {k.people.map((x: any) => (
                  <tr key={x.userId}>
                    <td>
                      <strong>{x.name}</strong>
                      {x.title && <div className="small muted">{x.title}</div>}
                      {(!x.hasEmail || !x.hasCell) && (
                        <button type="button" className="btn link small" onClick={() => setEditing(editing === x.userId ? null : x.userId)}>
                          {!x.hasEmail && !x.hasCell ? 'Add email / cell' : !x.hasCell ? 'Add cell' : 'Add email'}
                        </button>
                      )}
                    </td>
                    <td>
                      <input type="checkbox" checked={x.byEmail} disabled={a.busy || (!x.hasEmail && !x.byEmail)} onChange={(e) => save(x.userId, e.target.checked, x.byText)} aria-label={`${k.label}: email ${x.name}`} />
                    </td>
                    <td>
                      <input type="checkbox" checked={x.byText} disabled={a.busy || (!x.hasCell && !x.byText)} onChange={(e) => save(x.userId, x.byEmail, e.target.checked)} aria-label={`${k.label}: text ${x.name}`} />
                    </td>
                    <td>
                      <Badge tone="ok">Always</Badge>
                    </td>
                    <td className="num">
                      <button type="button" className="btn link small" disabled={a.busy} onClick={() => a.run(async () => (await api.post(`/api/agency-alerts/subscriptions/${x.userId}/${k.kind}/remove`), onChange()))} aria-label={`Stop sending ${k.label} to ${x.name}`}>
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {editing && person(editing) && <ContactEdit p={person(editing)} carriers={carriers} onDone={() => (setEditing(null), onChange())} />}
        <form
          className="al-add"
          onSubmit={(e) => {
            e.preventDefault();
            const p = person(add.userId);
            save(add.userId, add.byEmail && !!p?.email, add.byText && !!p?.cell);
            setAdd({ userId: '', byEmail: true, byText: false });
          }}
        >
          <select value={add.userId} onChange={(e) => setAdd({ ...add, userId: e.target.value })} aria-label={`Add someone to ${k.label}`} required>
            <option value="">Add someone…</option>
            {people
              .filter((p) => !taken.has(p.id))
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                  {p.title ? ` — ${p.title}` : ''}
                </option>
              ))}
          </select>
          {add.userId && (
            <>
              <label className="check">
                <input type="checkbox" checked={add.byEmail} disabled={!person(add.userId)?.email} onChange={(e) => setAdd({ ...add, byEmail: e.target.checked })} /> Email{!person(add.userId)?.email && ' (no email on file)'}
              </label>
              <label className="check">
                <input type="checkbox" checked={add.byText} disabled={!person(add.userId)?.cell} onChange={(e) => setAdd({ ...add, byText: e.target.checked })} /> Text{!person(add.userId)?.cell && ' (no cell on file)'}
              </label>
              {(!person(add.userId)?.email || !person(add.userId)?.cell) && (
                <button type="button" className="btn link small" onClick={() => setEditing(add.userId)}>
                  Add their email / cell
                </button>
              )}
            </>
          )}
          <button className="btn small primary" disabled={a.busy || !add.userId}>
            Add
          </button>
        </form>
        <ErrorBox error={a.error} />
      </div>
    </Card>
  );
}

export function AlertsSetup() {
  const r = useLoad(() => api.get<any>('/api/agency-alerts/setup'));
  const recent = useLoad(() => api.get<any>('/api/agency-alerts/recent'));
  const [showRecent, setShowRecent] = useState(false);
  return (
    <div className="ops stack">
      <PageHeader crumb="Agency" title="Alerts" sub="Every alert BlueHQ sends. Pick who gets each one and how — email, text, or both. Everyone you pick also sees it on their Home." />
      {r.data?.textMode === 'logged' && <Notice tone="warn">Texts aren’t set up for this site yet, so text alerts are recorded but not sent. Email and Home alerts work now.</Notice>}
      {r.error ? (
        <ErrorBox error={r.error} />
      ) : !r.data ? (
        <Spinner />
      ) : (
        <div className="al-kinds">
          {r.data.kinds.map((k: any) => (
            <KindCard key={k.kind} k={k} people={r.data.people} carriers={r.data.carriers ?? []} onChange={() => (r.reload(), recent.reload())} />
          ))}
        </div>
      )}
      <Card title="Recently sent" actions={<button className="btn small" onClick={() => setShowRecent(!showRecent)}>{showRecent ? 'Hide' : 'Show'}</button>}>
        {showRecent &&
          (!recent.data ? (
            <Spinner />
          ) : !recent.data.alerts.length ? (
            <p className="small muted">No alerts sent yet.</p>
          ) : (
            <ul className="plain al-recent">
              {recent.data.alerts.map((x: any) => (
                <li key={x.id}>
                  <strong>{x.title}</strong> <span className="small muted">· {ago(x.created_at)}</span>
                  <div className="small">{(x.sent_to ?? []).map((t: any) => `${t.name} (${t.channels.join(', ')}${t.seen ? ', seen' : ''})`).join('; ') || 'No one signed up'}</div>
                </li>
              ))}
            </ul>
          ))}
      </Card>
    </div>
  );
}

export function NarcanPage() {
  const { navigate } = useRouter();
  const r = useLoad(() => api.get<any>('/api/narcan'));
  const a = useAction();
  const uses: any[] = r.data?.uses ?? [];
  const toVerify = uses.filter((u) => u.canVerify);
  return (
    <div className="ops stack">
      <PageHeader crumb="Reports" title="Narcan" sub="Narcan used on calls. When a deputy gives Narcan they fill in the Narcan use form and keep a copy; their supervisor confirms it was done." />
      {r.error ? (
        <ErrorBox error={r.error} />
      ) : !r.data ? (
        <Spinner />
      ) : !uses.length ? (
        <EmptyState icon="check" title="No Narcan uses yet" />
      ) : (
        <>
          {toVerify.length > 0 && <Notice tone="warn">{toVerify.length} Narcan form{toVerify.length === 1 ? '' : 's'} for you to verify.</Notice>}
          <ErrorBox error={a.error} />
          <div className="card flush">
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Case</th>
                    <th>Deputy</th>
                    <th>Given by</th>
                    <th>Form</th>
                    <th>Supervisor</th>
                    <th>Verified</th>
                  </tr>
                </thead>
                <tbody>
                  {uses.map((u) => (
                    <tr key={u.id}>
                      <td>
                        <button type="button" className="btn link mono" onClick={() => navigate(at(`/reports/${u.incident_id}`))}>
                          {u.case_number}
                        </button>
                        <div className="small muted">{day(u.created_at)}</div>
                      </td>
                      <td>{u.deputy_name}</td>
                      <td>
                        {u.givenByLabel}
                        {u.doses ? <div className="small muted">{u.doses} dose{u.doses == 1 ? '' : 's'}</div> : null}
                      </td>
                      <td>{u.given_by === 'me' ? u.form_done ? <Badge tone="ok">Deputy says done</Badge> : <Badge tone="bad">Not done</Badge> : <span className="small muted">Another person’s form</span>}</td>
                      <td>{u.supervisor_name ?? <span className="muted">—</span>}</td>
                      <td>
                        {u.verified_at ? (
                          <>
                            <Badge tone="ok">Verified</Badge>
                            <div className="small muted">
                              {u.verified_by_name} · {day(u.verified_at)}
                            </div>
                          </>
                        ) : u.canVerify ? (
                          <button
                            type="button"
                            className="btn small primary"
                            disabled={a.busy}
                            onClick={() => {
                              const note = window.prompt('Verified the Narcan form was completed. Add a note (optional):') ;
                              if (note !== null) a.run(async () => (await api.post(`/api/narcan/${u.id}/verify`, { note: note || null }), r.reload()));
                            }}
                          >
                            Verify form
                          </button>
                        ) : (
                          <Badge tone="warn">Waiting</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
